/**
 * ChildAssetsTable — assets inherited from child TARAs (hierarchical
 * mode). Read-only data + two interactive columns:
 *   - Relevant (checkbox) — user marks the child asset as in/out of
 *     scope for the parent TARA
 *   - Justification (text input) — free-text reason for the choice
 *
 * Modifications are owned by the parent (HierarchicalAssetReview);
 * this table just renders the merged "view" rows and dispatches
 * change events.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  DataTable,
  TableToolbar,
  TablePagination,
} from '../../../../components/ui/DataTable';
import RelevanceCheckmark from '../../components/RelevanceCheckmark';
import RelevanceEditToggle from '../../components/RelevanceEditToggle';
import type { AssetRecord } from './assetFieldSchema';
import styles from './ChildAssetsTable.module.css';

const DataTableAny = DataTable as unknown as React.ComponentType<any>;
const TableToolbarAny = TableToolbar as unknown as React.ComponentType<any>;
const TablePaginationAny = TablePagination as unknown as React.ComponentType<any>;

const DEFAULT_PAGE_SIZE = 20;

const joinList = (v: unknown): string =>
  Array.isArray(v) ? v.join(', ') : v == null ? '' : String(v);

export interface ChildAssetView extends AssetRecord {
  source?: string;
  source_session_id?: string;
  is_relevant?: boolean;
  relevance_justification?: string;
}

export interface ChildAssetsTableProps {
  assets: ChildAssetView[];
  onToggleRelevance: (assetId: string) => void;
  onJustificationChange: (assetId: string, justification: string) => void;
  disabled?: boolean;
  /** Persist relevance modifications on exit-edit. Story 15.F —
   *  wired by the Step 2 hook to `hierarchical.onApprove`, which
   *  only saves via `approveHierarchicalData` (no workflow advance). */
  onSave?: () => Promise<void> | void;
}

export default function ChildAssetsTable({
  assets,
  onToggleRelevance,
  onJustificationChange,
  disabled,
  onSave,
}: ChildAssetsTableProps) {
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  // Effective edit mode — local toggle gated by the global `disabled`
  // (true while the unified approve is in-flight) AND the local save.
  // Matches legacy ChildAssetsSection's behavior: edit button is itself
  // disabled, and any open edit collapses back to read-only display.
  const liveEditing = editing && !disabled && !saving;

  const handleToggle = async () => {
    if (editing) {
      // Exiting edit mode — persist modifications first (legacy
      // "Confirm" semantic). Failure surfaces via the hook's
      // approveError toast; we still drop out of edit mode so the
      // user isn't stuck in a half-edited state.
      if (onSave) {
        setSaving(true);
        try {
          await onSave();
        } finally {
          setSaving(false);
        }
      }
      setEditing(false);
    } else {
      setEditing(true);
    }
  };

  // Story 14.B — match parent AssetTable's search-everything behavior
  // so child tables expose discoverable filtering chrome even when
  // empty.
  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return assets;
    return assets.filter((r) =>
      Object.values(r).some((v) =>
        v == null
          ? false
          : Array.isArray(v)
          ? v.join(' ').toLowerCase().includes(q)
          : String(v).toLowerCase().includes(q),
      ),
    );
  }, [assets, search]);

  const totalPages = Math.max(1, Math.ceil(searched.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const paged = useMemo(
    () => searched.slice((page - 1) * pageSize, page * pageSize),
    [searched, page, pageSize],
  );

  const columns = useMemo(
    () => [
      { key: 'asset_id', label: 'ID', sortable: true, width: '9%' },
      { key: 'asset_name', label: 'Name', sortable: true, width: '18%' },
      { key: 'asset_type', label: 'Type', sortable: true, width: '10%' },
      { key: 'asset_description', label: 'Description', sortable: false, width: '24%' },
      {
        key: 'security_properties',
        label: 'Security Properties',
        sortable: false,
        width: '15%',
        render: (row: ChildAssetView) => joinList(row.security_properties),
      },
      {
        key: '_relevance',
        label: 'Relevant',
        sortable: false,
        align: 'center',
        width: '8%',
        render: (row: ChildAssetView) =>
          liveEditing ? (
            <input
              type="checkbox"
              className={styles.relevanceCheckbox}
              checked={row.is_relevant !== false}
              onChange={() => onToggleRelevance(row.asset_id)}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Mark ${row.asset_name || row.asset_id} as relevant`}
            />
          ) : (
            <RelevanceCheckmark relevant={row.is_relevant !== false} />
          ),
      },
      {
        key: '_justification',
        label: 'Justification',
        sortable: false,
        width: '16%',
        render: (row: ChildAssetView) =>
          liveEditing ? (
            <input
              type="text"
              className={styles.justificationInput}
              value={row.relevance_justification ?? ''}
              placeholder="Why? (optional)"
              onChange={(e) => onJustificationChange(row.asset_id, e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span>{row.relevance_justification || '—'}</span>
          ),
      },
    ],
    [onToggleRelevance, onJustificationChange, liveEditing],
  );

  return (
    <DataTableAny
      columns={columns}
      data={paged}
      rowKey="asset_id"
      emptyMessage="No inherited assets from child TARAs."
      toolbar={
        <TableToolbarAny
          search={{
            value: search,
            onChange: (v: string) => { setSearch(v); setPage(1); },
            placeholder: 'Search inherited assets…',
          }}
          count={{ value: searched.length, label: 'Inherited Assets' }}
          showFilterButton={false}
          actions={
            <RelevanceEditToggle
              editing={liveEditing}
              onToggle={handleToggle}
              disabled={disabled}
              pending={saving}
            />
          }
        />
      }
      pagination={
        <TablePaginationAny
          currentPage={page}
          totalItems={searched.length}
          itemsPerPage={pageSize}
          onPageChange={setPage}
          onItemsPerPageChange={(size: number) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      }
    />
  );
}
